import { useEffect, useState } from "react";

import type { ICaaOrder } from "../types/api/customer-account";
import { OrderStatus, OrderStatusButtonType } from "../types/enum";
import { hasPreorderCaa, orderHasTrackingCaa } from "../utils/order";
import useCaaProductTags from "./useCaaProductTags";

// classic useOrderStatus와 동일 기준 — KR 일반 주문은 14일, 그 외(프리오더, en/jp)는 31일
const RETURN_MAX_DATE_DAYS = 31;
const KR_RETURN_MAX_DATE_DAYS = 14;

const isReturnPeriodValidCaa = (order: ICaaOrder, maxDays: number): boolean => {
  const diffInDays = (Date.now() - new Date(order.processedAt).getTime()) / (24 * 60 * 60 * 1000);
  return diffInDays < maxDays;
};

/**
 * classic useOrderStatus의 CAA 대응
 *
 * - 선물하기 여부: 판별 불가 — 선물하기 세부 분기(Gifting_Pending/Accepted)는 생략 (아래 TODO 참고)
 * - 프리오더 여부: CAA 라인엔 상품 태그가 없어 productId로 Storefront API에서 태그를 조회(useCaaProductTags)
 *   태그 조회가 끝나기 전엔 상태를 정하지 않음 (일반 주문으로 먼저 보였다가 바뀌는 것 방지)
 *
 * 취소/반품을 제공하지 않는 스토어는 allowCancel/allowReturn을 false로 넘김 — 버튼만 NONE이 되고 orderStatus 텍스트는 그대로 노출됨
 *
 * TODO: 선물하기 분기 복구 — order.customAttributes(isGifting, returnStatus)를 볼 수 있게 되면 classic과 동일하게 맞춰야 함
 *   classic은 Storefront API의 order.customAttributes로 선물하기 여부를 판별하지만, CAA로는 이 값을 가져올 수 없음
 *   - CAA: 실제 엔드포인트에서 Order.customAttributes 조회 시 "Field 'customAttributes' doesn't exist on type 'Order'" 에러
 *     (hydrogen-react 2024.1 스키마엔 있지만 디스커버리로 받는 실제 API 버전엔 없음)
 *   → 주문 생성 시 백엔드(webhook/Flow)에서 isGifting을 주문 메타필드로 복사하거나 태그 생성 해야함
 */
export default function useOrderStatusCaa(
  order?: ICaaOrder | null,
  { allowCancel = true, allowReturn = true, isKR = false }: { allowCancel?: boolean; allowReturn?: boolean; isKR?: boolean } = {},
) {
  const [activeButton, setActiveButton] = useState<OrderStatusButtonType>(OrderStatusButtonType.NONE);
  const [orderStatus, setOrderStatus] = useState<OrderStatus>(OrderStatus.None);

  const { tagsByProductId, isFetched: isTagsFetched } = useCaaProductTags(order);

  const handleOrderStatus = (type: OrderStatusButtonType, status: OrderStatus) => {
    setActiveButton(type);
    setOrderStatus(status);
  };

  useEffect(() => {
    if (!order || !isTagsFetched) return;

    // classic은 customAttributes(returnStatus)를 먼저 보고 없으면 메타필드를 보지만, CAA는 customAttributes를 못 가져와 메타필드만 본다 (TODO 참고)
    const returnStatus = order.returnStatus?.value;
    const isOffline = Boolean(order.isOffline?.value);
    const isCanceled = Boolean(order.cancelReason);
    const isFamilySale = order.isfamilysale?.value === "true";
    const isPreorder = hasPreorderCaa(order, tagsByProductId);
    const returnMaxDays = isKR && !isPreorder ? KR_RETURN_MAX_DATE_DAYS : RETURN_MAX_DATE_DAYS;

    const cancelOrNone = allowCancel ? OrderStatusButtonType.CANCEL : OrderStatusButtonType.NONE;
    const returnOrNone = () =>
      allowReturn && isReturnPeriodValidCaa(order, returnMaxDays) ? OrderStatusButtonType.RETURN : OrderStatusButtonType.NONE;

    const orderStatusHandlers: Record<"preOrder" | "regularOrder", Partial<Record<string, () => void>>> = {
      regularOrder: {
        PAID: () => {
          // 고객이 주문 완료 후 '일부 상품'을 hold 했을때
          // TODO: classic은 선물하기 주문이면 Gifting_Pending — isGifting 판별이 가능해지면 복구
          if (order.fulfillmentStatus === "ON_HOLD") {
            handleOrderStatus(cancelOrNone, OrderStatus.Completed);
            return;
          }

          // 고객이 주문 완료 후 아직 비젬 주문 확정 전일 때
          if (order.fulfillmentStatus === "UNFULFILLED") {
            // 주문 금액이 0원 (이벤트성 할인쿠폰)인데 주문 취소 했을 때
            if (isCanceled) {
              handleOrderStatus(OrderStatusButtonType.NONE, OrderStatus.Canceled);
              return;
            }

            // TODO: classic은 선물하기 주문이면 받는 사람 수락 여부(giftingIsAccepted)에 따라
            //   수락 시 NONE + Gifting_Accepted, 미수락 시 CANCEL + Gifting_Pending — isGifting 판별이 가능해지면 복구
            handleOrderStatus(cancelOrNone, OrderStatus.Completed);
            return;
          }

          if (order.fulfillmentStatus !== "FULFILLED") return;

          // 고객이 매장에서 주문 시
          if (isOffline) {
            handleOrderStatus(OrderStatusButtonType.NONE, OrderStatus.Completed);
            return;
          }

          // 반품 신청을 했을 때
          if (returnStatus === "requested") {
            handleOrderStatus(returnOrNone(), OrderStatus.Return_Requested);
            return;
          }

          // 반품 신청이 승인 되었을 때
          if (returnStatus === "approved") {
            handleOrderStatus(returnOrNone(), OrderStatus.Return_Approved);
            return;
          }

          // 패밀리세일에 구매한 주문이 배송완료 되었을 때 — 반품 불가
          if (orderHasTrackingCaa(order) && isFamilySale) {
            handleOrderStatus(OrderStatusButtonType.NONE, OrderStatus.Shipped);
            return;
          }

          // 비젬에서 송장 전송까지 했을 때 — 이미 발송되어 취소 불가
          if (orderHasTrackingCaa(order)) {
            handleOrderStatus(returnOrNone(), OrderStatus.Shipped);
            return;
          }

          // 1. 고객이 주문 완료 후 비젬에서 주문 확정했을 때
          // 2. 드로우 주문: 당첨된 오프라인 드로우 주문이면서 비젬에서 주문 확정했을 때
          handleOrderStatus(OrderStatusButtonType.NONE, OrderStatus.Preparing);
        },
        PARTIALLY_PAID: () => {
          if (order.fulfillmentStatus !== "FULFILLED") return;

          // 반품된 라인아이템이 이미 존재하는 경우 추가로 반품 신청을 했을 때
          if (returnStatus === "requested") {
            handleOrderStatus(returnOrNone(), OrderStatus.Return_Requested);
            return;
          }

          // 반품된 라인아이템이 이미 존재하는 경우 추가로 반품 승인이 되었을 때
          if (returnStatus === "approved") {
            handleOrderStatus(returnOrNone(), OrderStatus.Return_Approved);
            return;
          }

          // 반품 처리중인 주문이 존재하지 않을 때
          handleOrderStatus(returnOrNone(), OrderStatus.Refunded);
        },
        PARTIALLY_REFUNDED: () => {
          if (order.fulfillmentStatus !== "FULFILLED") return;

          if (returnStatus === "requested") {
            handleOrderStatus(returnOrNone(), OrderStatus.Return_Requested);
            return;
          }

          if (returnStatus === "approved") {
            handleOrderStatus(returnOrNone(), OrderStatus.Return_Approved);
            return;
          }

          // 모든 주문이 반품되었지만 반품배송비때문에 부분환불로 보이는 경우 포함
          handleOrderStatus(returnOrNone(), OrderStatus.Partially);
        },
        REFUNDED: () => {
          // 고객 또는 CS팀에서 주문을 취소했을 때 / 반품 후 환불 처리까지 완료했을 때 / 매장 구매 후 환불한 경우
          handleOrderStatus(OrderStatusButtonType.NONE, isCanceled ? OrderStatus.Canceled : OrderStatus.Refunded);
        },
        VOIDED: () => {
          // 결제 승인만 된 상태에서, 실제로 금액이 청구되기 전에 취소되었을때
          handleOrderStatus(OrderStatusButtonType.NONE, OrderStatus.Canceled);
        },
        PENDING: () => {
          // 아직 결제가 진행중인 경우
          handleOrderStatus(OrderStatusButtonType.NONE, OrderStatus.Pending);
        },
      },
      preOrder: {
        PAID: () => {
          // 고객이 주문 완료 후 아직 비젬 주문 확정 전일 때
          if (order.fulfillmentStatus === "UNFULFILLED") {
            handleOrderStatus(
              isCanceled ? OrderStatusButtonType.NONE : cancelOrNone,
              isCanceled ? OrderStatus.Canceled : OrderStatus.Completed,
            );
            return;
          }

          if (order.fulfillmentStatus === "FULFILLED") {
            // 반품 신청을 했을 때
            if (returnStatus === "requested") {
              handleOrderStatus(returnOrNone(), OrderStatus.Return_Requested);
              return;
            }

            // 반품 신청이 승인 되었을 때
            if (returnStatus === "approved") {
              handleOrderStatus(returnOrNone(), OrderStatus.Return_Approved);
              return;
            }

            // 고객이 주문 완료 후 비젬에서 송장 전송까지 했을 때
            if (orderHasTrackingCaa(order)) {
              handleOrderStatus(returnOrNone(), OrderStatus.Completed_Preparing);
              return;
            }

            // 1. 고객이 주문 완료 후 비젬에서 주문 확정했을 때
            // 2. 드로우 주문: 당첨된 오프라인 드로우 주문이면서 비젬에서 주문 확정했을 때
            handleOrderStatus(OrderStatusButtonType.NONE, OrderStatus.Scheduled_Preparing);
            return;
          }

          // OnHold 상태인 경우
          if (order.fulfillmentStatus === "ON_HOLD") {
            handleOrderStatus(cancelOrNone, OrderStatus.Scheduled);
          }
        },
        REFUNDED: () => {
          // 고객 또는 CS팀에서 주문을 취소했을 때 / 모든 제품에 대해 반품 후 환불 처리까지 완료했을 때
          handleOrderStatus(OrderStatusButtonType.NONE, isCanceled ? OrderStatus.Canceled : OrderStatus.Refunded);
        },
        VOIDED: () => {
          // 결제 승인만 된 상태에서, 실제로 금액이 청구되기 전에 취소되었을때
          handleOrderStatus(OrderStatusButtonType.NONE, OrderStatus.Canceled);
        },
        PENDING: () => {
          // 아직 결제가 진행중인 경우
          handleOrderStatus(OrderStatusButtonType.NONE, OrderStatus.Pending);
        },
      },
    };

    // AUTHORIZED(결제 승인만 된 상태)는 PAID와 동일하게 처리
    const financialStatus = order.financialStatus === "AUTHORIZED" ? "PAID" : order.financialStatus;
    const orderType = isPreorder ? "preOrder" : "regularOrder";

    // 핸들러가 없는 상태(EXPIRED 등)는 그대로 둔다
    if (financialStatus) orderStatusHandlers[orderType][financialStatus]?.();
  }, [order, isTagsFetched, tagsByProductId, allowCancel, allowReturn, isKR]);

  return { activeButton, orderStatus };
}
